import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  authCheck,
  authLogin,
  authLogout,
  type BiAuthUser,
} from '../api/auth'
import { resetSessaoFinanceira } from '../api/bridge'

type BiAuthContextValue = {
  user: BiAuthUser | null
  isAuthenticated: boolean
  bootstrapping: boolean
  loading: boolean
  error: string | null
  login: (email: string, senha: string) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
  refreshSession: () => Promise<boolean>
  updateUser: (next: BiAuthUser) => void
}

const BiAuthContext = createContext<BiAuthContextValue | null>(null)

export function BiAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<BiAuthUser | null>(null)
  const [bootstrapping, setBootstrapping] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshSession = useCallback(async () => {
    try {
      const u = await authCheck()
      setUser(u)
      return Boolean(u)
    } catch {
      setUser(null)
      return false
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const u = await authCheck()
        if (!cancelled) setUser(u)
      } catch {
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setBootstrapping(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (email: string, senha: string) => {
    setLoading(true)
    setError(null)
    try {
      const u = await authLogin(email.trim(), senha)
      resetSessaoFinanceira()
      setUser(u)
    } catch (e) {
      setUser(null)
      setError(e instanceof Error ? e.message : 'Falha no login')
      throw e
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    setLoading(true)
    try {
      await authLogout()
    } finally {
      resetSessaoFinanceira()
      setUser(null)
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])
  const updateUser = useCallback((next: BiAuthUser) => setUser(next), [])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      bootstrapping,
      loading,
      error,
      login,
      logout,
      clearError,
      refreshSession,
      updateUser,
    }),
    [
      user,
      bootstrapping,
      loading,
      error,
      login,
      logout,
      clearError,
      refreshSession,
      updateUser,
    ],
  )

  return <BiAuthContext.Provider value={value}>{children}</BiAuthContext.Provider>
}

export function useBiAuth() {
  const ctx = useContext(BiAuthContext)
  if (!ctx) throw new Error('useBiAuth fora de BiAuthProvider')
  return ctx
}
